import type {ReactElement} from 'react';
import React, {useState, useEffect, useCallback} from 'react';
import type {AssistantMessageRowProps} from './assistant-message-row.types';
import styles from './assistant-message-row.module.scss';
import MarkdownRenderer from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {PublishingOptionsList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import type {PromiseVoidFunction} from '@/types/global';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {IconSize} from '@Components/icon-v2/icon.types';
import {getEventWizardURLWithDesignPrefill} from '@Libraries/events.library';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import Divider from '@Components/divider';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {TextSize} from '@Components/text';
import {PillButton} from '@Components/pill-button';
import {SHARE_PANEL_ID, ShareItemType} from '@Panels/share-panel/share-panel.types';
import {openSharingModal} from '@Modals/share-modal/share-modal-library';
import {ThreeDotsLoader} from '@Components/three-dots-loader';
import {DOTS_COLOR} from '@Components/three-dots-loader/three-dots-loader';
import {ChatImage} from '@Components/ai-chat-window/components/chat-image/chat-image';
import {getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';

enum DesignAction {
  DOWNLOAD = 'download',
  PUBLISH = 'publish',
  OPEN_IN_POSTER_DITOR = 'open_in_poster_editor',
  MORE_OPTIONS = 'more_options',
}

export function AssistantMessageRow({...props}: AssistantMessageRowProps): ReactElement {
  const [loadingAction, setLoadingAction] = useState<{
    imageId: string | null;
    action: DesignAction | null;
  }>({imageId: null, action: null});

  const imageParts = (props.message.parts ?? []).filter((part) => part.type === 'image');
  const [loadedCount, setLoadedCount] = useState(0);
  const allImagesLoaded = loadedCount === imageParts.length && imageParts.length > 0;

  useEffect(() => {
    setLoadedCount(0);
  }, [props.message.parts]);

  const handleImageLoad = useCallback(() => {
    setLoadedCount((prev) => {
      const newCount = prev + 1;
      if (newCount === imageParts.length) {
        props.onAllImagesLoaded?.();
      }
      return newCount;
    });
  }, [imageParts.length, props.onAllImagesLoaded]);

  const getPublishingOptionListItem = (key: PublishingOptionsList, iconName: string, heading: string, onClick: PromiseVoidFunction): ReactElement => {
    return (
      <PublishingOptionListItem
        key={key}
        iconName={iconName}
        heading={heading}
        thumbNailType={ThumbNailType.ICON}
        onClick={onClick}
        customThumbnailClasses={styles.publishingThumbnail}
        className={styles.publishingOption}
        headingTextClass={styles.publishingOptionHeadingText}
        headingContainerClass={styles.publishingOptionHeadingContainerClass}
        iconSize={IconSize.SIZE_ICON_16}
        iconClasses={styles.publishingOptionThumbnailIcon}
      />
    );
  };

  const beginAction = (imageId: string, action: DesignAction): void => {
    setLoadingAction({imageId, action});
  };

  const endAction = (): void => {
    setLoadingAction({imageId: null, action: null});
  };

  const getPosterHashedIdForAiChatImageAsset = async (imageHashedId: string): Promise<string> => {
    return (await window.PMW.writeLocal('ai/getPosterForAiChatImageAsset', {
      imageHashedId: imageHashedId,
      chatId: props.chatId,
    })) as string;
  };

  const publishToSocialMedia = async (imageHashedId: string): Promise<void> => {
    beginAction(imageHashedId, DesignAction.PUBLISH);
    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(imageHashedId);
      window.location.href = window.PMW.util.site_url(`socialmedia/post/design/${posterHashedId}`);
    } catch (e) {
      console.error(e);
    } finally {
      endAction();
    }
  };

  const handleDownload = async (imageHashedId: string): Promise<void> => {
    beginAction(imageHashedId, DesignAction.DOWNLOAD);
    try {
      const url = getUrlForGeneratedAiChatModelAssetsImage(imageHashedId, MediaItemSizeType.HIGH_RES);

      const response = await fetch(url, {mode: 'cors'});
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = '123.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error(e);
    } finally {
      endAction();
    }
  };

  const promoteAsEvent = async (imageHashedId: string): Promise<void> => {
    beginAction(imageHashedId, DesignAction.PUBLISH);
    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(imageHashedId);
      window.open(getEventWizardURLWithDesignPrefill(posterHashedId, MediaTypes.MY_DESIGNS), '_blank');
    } catch (e) {
      console.error(e);
    } finally {
      endAction();
    }
  };

  const publishAsEmailCampaign = async (imageHashedId: string): Promise<void> => {
    beginAction(imageHashedId, DesignAction.PUBLISH);
    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(imageHashedId);
      if (window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG) {
        openUpsellingDialogForEmailCampaigns();
      } else {
        window.open(window.PMW.util.site_url(`emailmarketing/publish/design/${posterHashedId}`), '_blank');
      }
    } catch (e) {
      console.error(e);
    } finally {
      endAction();
    }
  };

  const openPosterInEditor = async (imageHashedId: string): Promise<void> => {
    beginAction(imageHashedId, DesignAction.OPEN_IN_POSTER_DITOR);
    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(imageHashedId);
      window.open(window.PMW.util.site_url(`posterbuilder/load/${posterHashedId}`), '_blank');
    } catch (e) {
      console.error(e);
    } finally {
      endAction();
    }
  };

  const shareDesign = async (imageHashedId: string): Promise<void> => {
    beginAction(imageHashedId, DesignAction.MORE_OPTIONS);
    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(imageHashedId);
      openSharingModal({
        itemHashedId: posterHashedId,
        type: ShareItemType.POSTER,
        panelId: SHARE_PANEL_ID,
      });
    } catch (e) {
      console.error(e);
    } finally {
      endAction();
    }
  };

  const getActionPromptPills = (key: string, pillText: string, prompt: string): ReactElement => {
    return (
      <PillButton
        key={key}
        text={pillText}
        size={PillButtonSize.SMALL}
        onClick={() => {
          console.log(prompt);
        }}
        textSize={TextSize.XXSMALL}
        isDisabled
      />
    );
  };

  const getActionsForGeneratedDesign = (imageHashedId: string, _posterHashedId?: string): ReactElement => {
    const isThisRowBusy = loadingAction.imageId === imageHashedId;
    const isDownloadLoading = isThisRowBusy && loadingAction.action === DesignAction.DOWNLOAD;
    const isPublishingLoading = isThisRowBusy && loadingAction.action === DesignAction.PUBLISH;
    const isEditingLoading = isThisRowBusy && loadingAction.action === DesignAction.OPEN_IN_POSTER_DITOR;
    const isMoreOptionsLoading = isThisRowBusy && loadingAction.action === DesignAction.MORE_OPTIONS;

    const isDisabled = isDownloadLoading || isPublishingLoading || isEditingLoading || isMoreOptionsLoading;

    return (
      <div className={'flexbox gap-2 flex-wrap'}>
        {/*<Dropdown popupClassName={styles.editingActionsPopup} selector={<Button type={Type.PRIMARY} size={Size.XSMALL} text={window.i18next.t('pmwjs_download')} icon={'icon-download'} />} popup={*/}
        {/* <div >*/}
        {/* <ControlledListItemType7 id={'standard-download-option'} text={'Standard'} textClasses={styles.downloadOptionText} customElement={<div>A x Bpx</div>} customElementClasses={styles.downloadSizeBox} className={styles.downloadOption} />*/}
        {/* <ControlledListItemType7 id={'large-download-option'} text={'Large'} textClasses={styles.downloadOptionText} customElement={<div>2A x 2Bpx</div>} customElementClasses={styles.downloadSizeBox} className={styles.downloadOption}/>*/}
        {/* </div>*/}
        {/*} />*/}
        <Button
          customClasses={'radius-8'}
          type={Type.PRIMARY}
          size={Size.XSMALL}
          text={window.i18next.t('pmwjs_download')}
          icon={'icon-download'}
          onClick={() => void handleDownload(imageHashedId)}
          isLoading={isDownloadLoading}
          disabled={isDisabled}
        />
        <Dropdown
          popupClassName={styles.editingActionsPopup}
          selector={
            <Button
              customClasses={'radius-8'}
              type={Type.SECONDARY}
              size={Size.XSMALL}
              text={'Publish'}
              icon={'icon-publish-arrow'}
              isLoading={isPublishingLoading}
              disabled={isDisabled}
            />
          }
          popup={
            <>
              {getPublishingOptionListItem(PublishingOptionsList.SOCIAL_MEDIA, 'icon-social-media', 'Post on Social Media', async () => {
                await publishToSocialMedia(imageHashedId);
              })}
              {getPublishingOptionListItem(PublishingOptionsList.PROMOTE_AS_EVENT, 'icon-event', window.i18next.t('pmwjs_promote_as_event'), async () => {
                await promoteAsEvent(imageHashedId);
              })}
              {getPublishingOptionListItem(PublishingOptionsList.EMAIL_CAMPAIGN, 'icon-envelope', 'Send email campaign', async () => {
                await publishAsEmailCampaign(imageHashedId);
              })}
            </>
          }
        />
        <Dropdown
          popupClassName={`${styles.editingActionsPopup} ${styles.makeChangesPopup}`}
          selector={
            <Button
              customClasses={'radius-8'}
              type={Type.SECONDARY}
              size={Size.XSMALL}
              text={'Make Changes'}
              icon={'icon-pencil'}
              isLoading={isEditingLoading}
              disabled={isDisabled}
            />
          }
          popup={
            <>
              <div className={`${styles.promptPillsContainer} spacing-p-t-3 spacing-p-r-3 spacing-p-b-1 spacing-p-l-3`}>
                {getActionPromptPills('change-background-prompt', '🖼️ Change background', 'Prompt ..')}
                {getActionPromptPills('add-or-replace-prompt', '🧁 Add or replace', 'Prompt ..')}
                {getActionPromptPills('update-color-prompt', '🎨 Update color', 'Prompt ..')}
                {getActionPromptPills('change-text-prompt', '✍️ Change text', 'Prompt ..')}
                {getActionPromptPills('magic-expand-prompy', '📏 Magic expand', 'Prompt ..')}
                {getActionPromptPills('improve-quality-prompt', '🪩 Improve quality', 'Prompt ..')}
                {getActionPromptPills('reimagine-image-prompy', '🎆 Reimagine image', 'Prompt ..')}
              </div>
              <Divider />
              <div className={'spacing-p-t-1 spacing-p-r-3 spacing-p-b-3 spacing-p-l-3'}>
                <ControlledListItemType7
                  id={'open-image-in-editor'}
                  text={'Open image in editor'}
                  icon={'icon-pencil'}
                  onClick={async () => {
                    await openPosterInEditor(imageHashedId);
                  }}
                  className={'radius-8'}
                />
              </div>
            </>
          }
        />
        <Dropdown
          popupClassName={styles.editingActionsPopup}
          selector={
            <Button
              customClasses={`radius-8 ${styles.moreOptionsButton} `}
              type={Type.SECONDARY}
              size={Size.XSMALL}
              icon={'icon-menu-dots'}
              isLoading={isMoreOptionsLoading}
              disabled={isDisabled}
            />
          }
          popup={
            <div>
              <ControlledListItemType7
                id={'share-design-option'}
                text={'Share'}
                icon={'icon-group_add'}
                onClick={() => {
                  void shareDesign(imageHashedId);
                }}
                className={'radius-8'}
              />
              <ControlledListItemType7 id={'save-iamge-to-design-option'} text={'Save in My Designs'} icon={'icon-folder'} className={'radius-8'} isDisabled />
              {props.isLastMessage ? (
                <ControlledListItemType7 id={'regenerate-image-option'} text={'Regenerate'} icon={'icon-rotate-acw'} className={'radius-8'} onClick={props.onRegenerate} />
              ) : undefined}
            </div>
          }
        />
      </div>
    );
  };

  const getTextPart = (part: {text?: string}): ReactElement | null => {
    if (part.text) {
      return <MarkdownRenderer content={part.text} />;
    }

    return <ThreeDotsLoader size={8} color={DOTS_COLOR.DARK} />;
  };

  const getAssistantMessage = (): ReactElement => {
    const parts = props.message.parts ?? [];

    return (
      <div className={styles.assistantMessage}>
        {parts.map((part, index) => {
          switch (part.type) {
            case 'text':
              return (
                <div key={`model-part-${index}`} className={`${props.bubbleClassNames} ${styles.assistantText}  ${props.message.isThinking ? styles.thinkingText : ''}`}>
                  {getTextPart(part)}
                </div>
              );
            case 'image':
              return (
                <div key={`model-part-${index}`}>
                  <ChatImage
                    src={getUrlForGeneratedAiChatModelAssetsImage(part.hashedId, MediaItemSizeType.HIGH_RES)}
                    className={styles.generatedResult}
                    alt="AI generated content"
                    onLoad={handleImageLoad}
                  />
                  {allImagesLoaded ? getActionsForGeneratedDesign(part.hashedId) : null}
                </div>
              );
            default:
              return null;
          }
        })}
      </div>
    );
  };

  return getAssistantMessage();
}
